import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send } from 'lucide-react';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';

export default function ParentChatBubble() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const endRef = useRef(null);

  const fetchUnread = async () => {
    try {
      const { data } = await api.get('/api/messages/unread-count');
      setUnread(data?.unread || 0);
    } catch { /* silent */ }
  };

  const fetchThread = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/messages/me');
      setMessages(data || []);
      setUnread(0);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchUnread();
    // Subscribe to SSE for instant updates
    const token = localStorage.getItem('craven_access_token');
    const base = `${process.env.REACT_APP_BACKEND_URL}/api/messages/stream`;
    const url = token ? `${base}?access_token=${encodeURIComponent(token)}` : base;
    const es = new EventSource(url, { withCredentials: true });
    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === 'new_message') {
          if (open) {
            // Already viewing — append message and auto-mark read via fetch
            setMessages((prev) => {
              if (prev.some((m) => m.id === data.message.id)) return prev;
              return [...prev, data.message];
            });
            // Mark as read on server
            api.get('/api/messages/me').catch(() => {});
          } else if (data.message.sender_role !== 'parent') {
            // Only bump unread when NOT from self
            setUnread((n) => n + 1);
            toast.info(`New message from ${data.message.sender_name || 'Tracey'}`);
          }
        }
      } catch { /* ignore */ }
    };
    es.onerror = () => { /* auto-reconnects */ };
    return () => { es.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open) fetchThread();
  }, [open]);

  useEffect(() => {
    if (open && endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const v = text.trim();
    if (!v || sending) return;
    setSending(true);
    try {
      await api.post('/api/messages', { text: v });
      setText('');
      await fetchThread();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  return (
    <>
      {/* Floating button */}
      <motion.button
        initial={{ scale: 0, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#FD5E53] hover:bg-[#E84E44] text-white shadow-xl flex items-center justify-center hover:-translate-y-1 transition-transform"
        data-testid="chat-bubble-btn"
      >
        <MessageCircle className="w-6 h-6" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1.5 bg-[#16A34A] text-white text-xs font-bold rounded-full flex items-center justify-center ring-2 ring-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </motion.button>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            className="fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] sm:w-96 h-[min(600px,80vh)] bg-white rounded-[2rem] shadow-2xl border border-slate-100 flex flex-col overflow-hidden"
            data-testid="chat-panel"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#FD5E53] to-[#F97316] p-4 text-white flex items-center justify-between">
              <div>
                <p className="font-nunito text-xs uppercase tracking-wider opacity-80">Message Tracey</p>
                <p className="font-fredoka text-lg font-semibold">Craven-it Support</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-2 rounded-full hover:bg-white/20"
                data-testid="chat-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 bg-[#FDFBF7]" data-testid="chat-messages">
              {loading ? (
                <div className="flex justify-center py-8">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="w-8 h-8 border-3 border-[#FD5E53] border-t-transparent rounded-full"
                  />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center mt-16">
                  <div className="inline-flex w-16 h-16 rounded-full bg-[#FFE4E6] items-center justify-center mb-3">
                    <MessageCircle className="w-8 h-8 text-[#FD5E53]" />
                  </div>
                  <p className="font-fredoka text-lg text-slate-800 mb-1">Start a conversation</p>
                  <p className="font-nunito text-sm text-slate-500 px-4">
                    Questions about your child's progress? Just ask!
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((m) => {
                    const isMe = m.sender_role === 'parent';
                    return (
                      <div key={m.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                          isMe
                            ? 'bg-[#FD5E53] text-white rounded-br-md'
                            : 'bg-white text-slate-800 rounded-bl-md shadow-sm border border-slate-100'
                        }`}>
                          {!isMe && (
                            <p className="font-fredoka text-xs font-semibold text-[#FD5E53] mb-0.5">
                              {m.sender_name || 'Tracey'}
                            </p>
                          )}
                          <p className="font-nunito text-sm whitespace-pre-wrap">{m.text}</p>
                          <p className={`font-nunito text-[10px] mt-1 ${isMe ? 'text-white/70' : 'text-slate-400'}`}>
                            {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                </div>
              )}
            </div>

            {/* Input */}
            <form onSubmit={send} className="p-3 border-t border-slate-100 bg-white flex gap-2">
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type a message..."
                maxLength={2000}
                disabled={sending}
                className="flex-1 rounded-full bg-slate-50 border-0 px-4 py-2.5 text-sm font-nunito focus:outline-none focus:ring-2 focus:ring-[#38BDF8]"
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={!text.trim() || sending}
                className="w-10 h-10 rounded-full bg-[#FD5E53] hover:bg-[#E84E44] text-white flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                data-testid="chat-send"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
